import FirebaseAdapter from "@wc/adapters/firebase";
import { PermissionsEnum, userHasPermissionInOrg } from "@wc/domain/authz";

/**
 * @typedef {import('@wc/domain/users/models').AuthnUser} AuthnUser
 */

/**
 * @typedef {object} InviteListItem
 * @property {string} id The invite token
 * @property {string} teamId The team ID
 * @property {Date} createdAt When the invite was created
 * @property {Date} expiresAt When the invite expires
 * @property {number} useCount How many times used
 * @property {number|null} maxUses Max uses (null = unlimited)
 * @property {boolean} isExpired Whether the invite has expired
 */

/**
 * Lists active invites for a team
 * @param {string} teamId The team ID
 * @param {string} organizationId The org ID (for permission check)
 * @param {AuthnUser} user The requesting user
 * @returns {Promise<InviteListItem[]>} The active invites
 */
export async function listTeamInvites(teamId, organizationId, user) {
  const hasPermission = await userHasPermissionInOrg(
    user,
    organizationId,
    PermissionsEnum.MANAGE_ORGANIZATION_MEMBERS,
  );

  if (!hasPermission) {
    throw new Error("You do not have permission to view invites");
  }

  const invites = await FirebaseAdapter().invites.listTeamInvites(teamId);
  const now = new Date();

  return invites.map((invite) => {
    const expiresAt = invite.expiresAt?.toDate
      ? invite.expiresAt.toDate()
      : new Date(invite.expiresAt);
    const createdAt = invite.createdAt?.toDate
      ? invite.createdAt.toDate()
      : new Date(invite.createdAt);

    return {
      id: invite.id,
      teamId: invite.teamId,
      createdAt,
      expiresAt,
      useCount: invite.useCount,
      maxUses: invite.maxUses,
      isExpired: expiresAt < now,
    };
  });
}
